import { apiV1, extractApiErrorMessage } from './api'

/**
 * Admin-side helper around the backend `/api/rooms/*` endpoints (no v3 prefix).
 *
 * The backend room routes only require `authenticate`, so any logged-in admin
 * can read and manage rooms for any school. This is what lets the course
 * creation wizard's inline "Manage room types" modal work on behalf of the
 * admin without the school owner being involved.
 */

export type RoomType = 'PRIVATE' | 'DOUBLE' | 'TRIPLE' | 'QUADRUPLE' | 'CUSTOM'

export interface Room {
  _id: string
  schoolId: string
  type: RoomType
  name: string
  capacity: number
  notes?: string
  images?: string[]
  video?: string
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

export interface CreateRoomData {
  type: RoomType
  name: string
  capacity: number
  notes?: string
  images?: string[]
  video?: string
  isActive?: boolean
}

export type UpdateRoomData = Partial<CreateRoomData>

class AdminRoomService {
  async getSchoolRooms(schoolId: string, isActive?: boolean): Promise<Room[]> {
    try {
      const params = isActive !== undefined ? { isActive: String(isActive) } : {}
      const response = await apiV1.get(`/rooms/school/${schoolId}`, { params })
      const rooms = response.data?.data ?? response.data ?? []
      return Array.isArray(rooms) ? rooms : []
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to load rooms'))
    }
  }

  async getRoomById(roomId: string): Promise<Room> {
    try {
      const response = await apiV1.get(`/rooms/${roomId}`)
      return response.data?.data ?? response.data
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to load room'))
    }
  }

  async createRoom(schoolId: string, data: CreateRoomData): Promise<Room> {
    try {
      const response = await apiV1.post(`/rooms/school/${schoolId}`, data)
      return response.data?.data ?? response.data
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to create room'))
    }
  }

  async updateRoom(roomId: string, data: UpdateRoomData): Promise<Room> {
    try {
      const response = await apiV1.put(`/rooms/${roomId}`, data)
      return response.data?.data ?? response.data
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to update room'))
    }
  }

  async deleteRoom(roomId: string): Promise<void> {
    try {
      await apiV1.delete(`/rooms/${roomId}`)
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to delete room'))
    }
  }

  async toggleRoomStatus(roomId: string): Promise<Room> {
    try {
      const response = await apiV1.patch(`/rooms/${roomId}/toggle-status`)
      return response.data?.data ?? response.data
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to toggle room status'))
    }
  }
}

export const adminRoomService = new AdminRoomService()
export default adminRoomService
